import React from "react";
import { motion } from "framer-motion";

/* ─── Pin SVG (Exact from reference) ─────────────────────── */
const Pin = ({ color = "#ec2b28", className = "" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill={color}
    className={className}
    style={{ filter: `drop-shadow(0 0 8px ${color}88)` }}
  >
    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
    <path d="M16 3a1 1 0 0 1 .117 1.993l-.117 .007v4.764l1.894 3.789a1 1 0 0 1 .1 .331l.006 .116v2a1 1 0 0 1 -.883 .993l-.117 .007h-4v4a1 1 0 0 1 -1.993 .117l-.007 -.117v-4h-4a1 1 0 0 1 -.993 -.883l-.007 -.117v-2a1 1 0 0 1 .06 -.34l.046 -.107l1.894 -3.791v-4.762a1 1 0 0 1 -.117 -1.993l.117 -.007h8z" />
  </svg>
);

/* ─── Card Component ─────────────────────────────────────── */
const Card = ({
  number,
  title,
  description,
  tag,
  colorTheme = "orange",
  className = "",
}) => {
  const themes = {
    orange: {
      bg: "#1e1313",
      text: "#ec2b28",
      border: "rgba(236, 43, 40, 0.35)",
      glow: "rgba(236, 43, 40, 0.15)",
    },
    blue: {
      bg: "#111726",
      text: "#60a5fa",
      border: "rgba(59, 130, 246, 0.35)",
      glow: "rgba(59, 130, 246, 0.15)",
    },
    purple: {
      bg: "#191224",
      text: "#c084fc",
      border: "rgba(168, 85, 247, 0.35)",
      glow: "rgba(168, 85, 247, 0.15)",
    },
    cyan: {
      bg: "#0e1a20",
      text: "#22d3ee",
      border: "rgba(6, 182, 212, 0.35)",
      glow: "rgba(6, 182, 212, 0.15)",
    },
  };

  const currentTheme = themes[colorTheme] || themes.orange;

  return (
    <div className={`hiw-card-wrapper ${className}`}>
      <div
        className="hiw-outer-card"
        style={{
          background: "#141417",
          padding: "10px",
          borderRadius: "26px",
          border: "1px solid rgba(255, 255, 255, 0.12)",
          boxShadow: `0 18px 40px rgba(0, 0, 0, 0.8), 0 0 20px ${currentTheme.glow}`,
          transition: "transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease",
          position: "relative",
          zIndex: 6,
        }}
      >
        <div style={{ display: "flex", justifyContent: "center", marginBottom: "14px", marginTop: "2px" }}>
          <Pin color={currentTheme.text} />
        </div>

        <div
          className="hiw-inner-card"
          style={{
            background: currentTheme.bg,
            border: `1px solid ${currentTheme.border}`,
            borderRadius: "16px",
            padding: "18px 20px 22px",
            display: "flex",
            flexDirection: "column",
            position: "relative",
            overflow: "hidden",
            minHeight: "170px",
            zIndex: 7,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "12px" }}>
            <span
              style={{
                color: currentTheme.text,
                fontSize: "2.4rem",
                fontFamily: '"Comic Sans MS", "Chalkboard SE", "Caveat", cursive, sans-serif',
                fontWeight: 700,
                lineHeight: 1,
              }}
            >
              {number}
            </span>
            {tag && (
              <span
                style={{
                  fontSize: "0.62rem",
                  fontWeight: 800,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  color: currentTheme.text,
                  background: "rgba(255, 255, 255, 0.08)",
                  padding: "4px 8px",
                  borderRadius: "6px",
                }}
              >
                {tag}
              </span>
            )}
          </div>

          <h3
            style={{
              fontFamily: "'Bricolage Grotesque', sans-serif",
              fontSize: "clamp(1.1rem, 1.5vw, 1.3rem)",
              fontWeight: 700,
              color: "#ffffff",
              lineHeight: 1.25,
              marginBottom: "10px",
              letterSpacing: "-0.02em",
            }}
          >
            {title}
          </h3>

          <p
            style={{
              fontFamily: "'Bricolage Grotesque', sans-serif",
              color: "rgba(255, 255, 255, 0.55)",
              fontSize: "0.85rem",
              lineHeight: 1.65,
              fontWeight: 400,
              margin: 0,
            }}
          >
            {description}
          </p>
        </div>
      </div>
    </div>
  );
};

/* ─── 6 Ecosystem Nodes ──────────────────────────────────── */
const STEPS = [
  {
    number: "01",
    title: "Growth",
    description: "Bring the right customers in through paid media, CRO, and performance channels.",
    tag: "↓ Generates",
    colorTheme: "orange",
  },
  {
    number: "02",
    title: "Commerce",
    description: "Convert demand wherever customers choose to buy across Shopify & marketplaces.",
    tag: "↓ Generates",
    colorTheme: "blue",
  },
  {
    number: "03",
    title: "Data",
    description: "Capture what customers and the business are telling you across every touchpoint.",
    tag: "↓ Creates",
    colorTheme: "purple",
  },
  {
    number: "04",
    title: "Intelligence",
    description: "Turn raw information into clearer commercial and strategic growth decisions.",
    tag: "↓ Informs",
    colorTheme: "cyan",
  },
  {
    number: "05",
    title: "Growth + Commerce",
    description: "Optimise campaigns, experiences and customer journeys with high-converting precision.",
    tag: "↓ Powered By",
    colorTheme: "orange",
  },
  {
    number: "06",
    title: "AI & Automation",
    description: "Remove friction, automate workflows and help the entire system move faster.",
    tag: "↺ Feeds Back",
    colorTheme: "purple",
  },
];

export default function HowEverythingConnects() {
  const height = 1380;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,200..800&display=swap');

        .hiw-section {
          background: #060608;
          font-family: 'Bricolage Grotesque', sans-serif;
          padding: clamp(50px, 8vh, 100px) clamp(16px, 4vw, 32px);
          border-top: 1px solid rgba(255, 255, 255, 0.08);
          color: #ffffff;
          position: relative;
          overflow: hidden;
        }

        /* Radial ambient glow */
        .hiw-glow {
          position: absolute;
          top: 30%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 600px;
          height: 600px;
          background: radial-gradient(circle, rgba(236, 43, 40, 0.08) 0%, transparent 70%);
          pointer-events: none;
          filter: blur(80px);
        }

        /* Header */
        .hiw-header {
          text-align: center;
          max-width: 800px;
          margin: 0 auto clamp(36px, 5vh, 56px);
          position: relative;
          z-index: 10;
        }
        .hiw-eyebrow {
          font-family: 'Bricolage Grotesque', sans-serif;
          font-size: 0.75rem;
          font-weight: 700;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: #ec2b28;
          display: inline-block;
          margin-bottom: 16px;
        }
        .hiw-title {
          font-family: 'Bricolage Grotesque', sans-serif;
          font-size: clamp(2.4rem, 5.5vw, 4.5rem);
          font-weight: 800;
          letter-spacing: -0.035em;
          color: #ffffff;
          line-height: 1.08;
          margin: 0 0 14px;
        }
        .hiw-title em {
          font-style: normal;
          color: #ec2b28;
        }
        .hiw-desc {
          font-family: 'Bricolage Grotesque', sans-serif;
          font-size: clamp(0.92rem, 1.25vw, 1.08rem);
          font-weight: 400;
          color: rgba(255, 255, 255, 0.65);
          line-height: 1.7;
          margin: 0 auto;
          max-width: 620px;
        }

        /* Card Board Container */
        .hiw-container {
          max-width: 1040px;
          margin: 0 auto;
          position: relative;
          z-index: 5;
        }

        .hiw-card-wrapper {
          position: relative;
          width: 100%;
          max-width: 320px;
          transition: transform 0.3s ease;
          z-index: 5;
        }
        .hiw-card-wrapper:hover {
          z-index: 30 !important;
          transform: scale(1.04) rotate(0deg) !important;
        }

        /* ── Desktop Absolute Zigzag Layout ── */
        @media (min-width: 820px) {
          .hiw-board {
            position: relative;
            width: 100%;
            height: ${height}px;
          }
          .hiw-pos-0 { position: absolute; top: 0px;    left: 10%;  transform: rotate(5deg); }
          .hiw-pos-1 { position: absolute; top: 180px;  right: 10%; transform: rotate(-5deg); }
          .hiw-pos-2 { position: absolute; top: 460px;  left: 10%;  transform: rotate(5deg); }
          .hiw-pos-3 { position: absolute; top: 640px;  right: 10%; transform: rotate(-5deg); }
          .hiw-pos-4 { position: absolute; top: 920px;  left: 10%;  transform: rotate(5deg); }
          .hiw-pos-5 { position: absolute; top: 1100px; right: 10%; transform: rotate(-5deg); }
        }

        /* ── Mobile & Tablet Clean Stack Layout (No Empty Space) ── */
        @media (max-width: 819px) {
          .hiw-board {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 20px;
            width: 100%;
            height: auto;
            position: relative;
          }
          .hiw-card-wrapper {
            position: relative !important;
            top: auto !important;
            left: auto !important;
            right: auto !important;
            transform: none !important;
            width: 100% !important;
            max-width: 440px !important;
            margin: 0 auto;
          }
        }

        /* Loopback and Manifesto Box */
        .hiw-manifesto {
          max-width: 960px;
          margin: clamp(40px, 6vh, 64px) auto 0;
          background: linear-gradient(160deg, #161619 0%, #0d0d0f 100%);
          border: 1px solid rgba(255, 255, 255, 0.12);
          border-radius: 24px;
          padding: clamp(24px, 4vw, 48px);
          position: relative;
          z-index: 10;
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.5), 0 0 30px rgba(236, 43, 40, 0.1);
        }
        .hiw-manifesto-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 0.65rem;
          font-weight: 800;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: #ec2b28;
          background: rgba(236, 43, 40, 0.12);
          border: 1px solid rgba(236, 43, 40, 0.3);
          padding: 5px 14px;
          border-radius: 99px;
          margin-bottom: 16px;
        }
        .hiw-manifesto-title {
          font-size: clamp(1.3rem, 2.4vw, 2rem);
          font-weight: 900;
          letter-spacing: -0.03em;
          color: #ffffff;
          line-height: 1.2;
          margin: 0 0 20px;
        }
        .hiw-manifesto-list {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
          margin-bottom: 24px;
        }
        .hiw-manifesto-item {
          font-size: 0.85rem;
          font-weight: 600;
          color: #e5e7eb;
          line-height: 1.5;
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid rgba(255, 255, 255, 0.07);
          padding: 12px 16px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .hiw-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #ec2b28;
          flex-shrink: 0;
          box-shadow: 0 0 8px #ec2b28;
        }
        .hiw-manifesto-footer {
          border-top: 1px solid rgba(255, 255, 255, 0.1);
          padding-top: 18px;
          font-size: clamp(0.95rem, 1.6vw, 1.15rem);
          font-weight: 800;
          color: #ffffff;
          letter-spacing: -0.02em;
          margin: 0;
        }
        .hiw-manifesto-footer em {
          font-style: normal;
          color: #ec2b28;
        }

        @media (max-width: 640px) {
          .hiw-manifesto-list {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <section className="hiw-section" id="how-it-works">
        <div className="hiw-glow" />

        {/* ── Section Header ── */}
        <div className="hiw-header">
          <span className="hiw-eyebrow">Visual Ecosystem</span>
          <h2 className="hiw-title">
            How Everything Connects
          </h2>
          <p className="hiw-desc">
            The Aurelix growth loop — six connected capabilities designed so every
            part of the business continuously learns from and accelerates the other.
          </p>
        </div>

        {/* ── Visual Pinned Board ── */}
        <div className="hiw-container">
          <div className="hiw-board">
            {/* Animated SVG Connector Line for Desktop */}
            <svg
              className="hidden md:block"
              viewBox={`0 0 1000 ${height}`}
              preserveAspectRatio="none"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                zIndex: 0,
                pointerEvents: "none",
              }}
            >
              <defs>
                <linearGradient id="flowGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#ec2b28" />
                  <stop offset="30%" stopColor="#3b82f6" />
                  <stop offset="60%" stopColor="#a855f7" />
                  <stop offset="100%" stopColor="#ec2b28" />
                </linearGradient>
              </defs>
              <motion.path
                d="M 260 140 C 520 140, 560 300, 740 300 C 920 300, 520 460, 260 580 C 80 700, 520 760, 740 760 C 920 760, 520 940, 260 1040 C 80 1140, 520 1220, 740 1220"
                stroke="url(#flowGradient)"
                strokeWidth="2.5"
                strokeDasharray="8 6"
                fill="none"
                strokeLinecap="round"
                initial={{ strokeDashoffset: 0 }}
                animate={{ strokeDashoffset: -140 }}
                transition={{
                  duration: 3.5,
                  repeat: Infinity,
                  ease: "linear",
                }}
                style={{ opacity: 0.7 }}
              />
            </svg>

            {/* 6 Pinned Cards */}
            {STEPS.map((step, index) => (
              <Card
                key={step.title}
                number={step.number}
                title={step.title}
                description={step.description}
                tag={step.tag}
                colorTheme={step.colorTheme}
                className={`hiw-pos-${index}`}
              />
            ))}
          </div>
        </div>

        {/* ── Bottom Manifesto Narrative Box ── */}
        <div className="hiw-manifesto">
          <span className="hiw-manifesto-badge">Continuous Compound Loop</span>
          <h3 className="hiw-manifesto-title">
            Your growth shouldn't live in separate departments.
          </h3>

          <div className="hiw-manifesto-list">
            <div className="hiw-manifesto-item">
              <span className="hiw-dot" />
              <span><strong>A campaign</strong> brings customers in.</span>
            </div>
            <div className="hiw-manifesto-item">
              <span className="hiw-dot" />
              <span><strong>Commerce</strong> turns demand into transactions.</span>
            </div>
            <div className="hiw-manifesto-item">
              <span className="hiw-dot" />
              <span><strong>Every interaction</strong> generates data.</span>
            </div>
            <div className="hiw-manifesto-item">
              <span className="hiw-dot" />
              <span><strong>Data</strong> creates intelligence.</span>
            </div>
            <div className="hiw-manifesto-item">
              <span className="hiw-dot" />
              <span><strong>Intelligence</strong> informs what you improve next.</span>
            </div>
            <div className="hiw-manifesto-item">
              <span className="hiw-dot" />
              <span><strong>AI & automation</strong> help the entire system move faster.</span>
            </div>
          </div>

          <p className="hiw-manifesto-footer">
            Because growth works better when <em>every part of the system learns from the other.</em>
          </p>
        </div>
      </section>
    </>
  );
}
